/**
 * <mobile-disclosure>
 *
 * Wraps a <details>. Below the md breakpoint it behaves as an animated
 * accordion that starts closed; from md up it is always open and its summary
 * is inert, so the same markup reads as a plain titled column.
 *
 *   <mobile-disclosure>
 *     <details open>
 *       <summary>…</summary>
 *       <div data-disclosure-panel>…</div>
 *     </details>
 *   </mobile-disclosure>
 *
 * Methods: open(), close(), toggle().
 */
(() => {
  if (customElements.get('mobile-disclosure')) return;

  const DURATION = 240;

  class MobileDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.panel = this.querySelector('[data-disclosure-panel]');
      if (!this.details || !this.summary || !this.panel) return;

      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.summary.addEventListener('click', (event) => this.onSummaryClick(event), { signal });
      window.addEventListener('on:breakpoint-change', () => this.sync(), { signal });

      // Theme editor: reveal the menu when its block is selected.
      const block = this.closest('[data-shopify-editor-block]');
      if (block) {
        block.addEventListener('shopify:block:select', () => this.collapsible && this.open(), { signal });
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      this.stopAnimation();
    }

    /** True while the element should act as an accordion. */
    get collapsible() {
      return !window.matchMedia(theme.mediaQueries.md).matches;
    }

    sync() {
      this.stopAnimation();
      const collapsible = this.collapsible;
      this.classList.toggle('is-collapsible', collapsible);
      this.details.open = !collapsible;
      if (collapsible) this.summary.removeAttribute('tabindex');
      else this.summary.setAttribute('tabindex', '-1');
    }

    onSummaryClick(event) {
      event.preventDefault();
      if (this.collapsible) this.toggle();
    }

    toggle() {
      if (this.details.open && !this.closing) this.close();
      else this.open();
    }

    open() {
      this.stopAnimation();
      this.details.open = true;
      this.animate([0, this.panel.scrollHeight]);
    }

    close() {
      if (!this.details.open) return;
      this.stopAnimation();
      this.closing = true;
      this.animate([this.panel.offsetHeight, 0], () => {
        this.details.open = false;
      });
    }

    /**
     * Runs the height animation. State is finalised by a timer rather than the
     * animation's own finish event, which browsers hold back while a page is
     * not being rendered.
     */
    animate([from, to], done) {
      const finish = () => {
        this.stopAnimation();
        if (done) done();
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion || typeof this.panel.animate !== 'function') {
        finish();
        return;
      }

      this.panel.style.overflow = 'hidden';
      this.animation = this.panel.animate(
        { height: [`${from}px`, `${to}px`], opacity: [from ? 1 : 0, to ? 1 : 0] },
        { duration: DURATION, easing: 'ease' }
      );
      this.timer = setTimeout(finish, DURATION);
    }

    stopAnimation() {
      clearTimeout(this.timer);
      if (this.animation) {
        this.animation.cancel();
        this.animation = null;
      }
      if (this.panel) this.panel.style.overflow = '';
      this.closing = false;
    }
  }

  customElements.define('mobile-disclosure', MobileDisclosure);
})();
